/**
 * Small building blocks of the project dashboard (founder concept: metrics(),
 * taskRow(), taskPanel(), memberList()). Real API data only.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import type { ProjectMember, StatusCount, Task } from '../../../packages/api-client/pm-api';
import { StatusIcon } from '../../../components/taskIcons';
import { formatDueDate } from '../../../utils/date';

export const STATUS_LABEL: Record<string, string> = {
    todo: 'To do', in_progress: 'In progress', review: 'In review', blocked: 'Blocked', done: 'Done',
};

export const memberName = (m: ProjectMember) => `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email;
const initials = (m: ProjectMember) =>
    (`${m.first_name?.[0] ?? ''}${m.last_name?.[0] ?? ''}` || m.email[0] || '?').toUpperCase();
const ROLE_LABEL: Record<ProjectMember['role'], string> = {
    owner: 'Project owner', admin: 'Project admin', member: 'Project member', viewer: 'Viewer',
};

/** Tasks · In progress · Needs review · Blocked, from the scoped status breakdown. */
export function MetricStrip({ breakdown, full }: { breakdown: StatusCount[]; full: boolean }) {
    const n = (s: string) => breakdown.find(b => b.status === s)?.count ?? 0;
    const tiles: [string, number][] = [
        [full ? 'Tasks' : 'Visible tasks', breakdown.reduce((sum, b) => sum + b.count, 0)],
        ['In progress', n('in_progress')], ['Needs review', n('review')], ['Blocked', n('blocked')],
    ];
    return (
        <dl className="pd-metrics" aria-label="Task counts">
            {tiles.map(([label, value]) => (
                <div key={label} className="pd-metric"><dt>{label}</dt><dd>{value}</dd></div>
            ))}
        </dl>
    );
}

export function StatusPill({ status }: { status: string }) {
    const tone = status === 'review' || status === 'blocked' || status === 'done' ? ` pd-pill--${status}` : '';
    return <span className={`pd-pill${tone}`}>{STATUS_LABEL[status] ?? status.replace(/_/g, ' ')}</span>;
}

export function TaskRow({ task, members }: { task: Task; members: ProjectMember[] }) {
    const assignee = members.find(m => m.user_id === task.assignee_id);
    const due = formatDueDate(task.due_date);
    return (
        <Link to={`/tasks/${task.id}`} className="pd-task-row" aria-label={`Open task: ${task.title}`}>
            <StatusIcon status={task.status} size={16} />
            <span className="pd-task-copy">
                <span className="pd-task-title">{task.parent_id && <span aria-label="Subtask">↳ </span>}{task.title}</span>
                {(assignee || due) && (
                    <span className="pd-task-sub">
                        {assignee && <span>{memberName(assignee)}</span>}
                        {due && <span>· Due {due}</span>}
                    </span>
                )}
            </span>
            <StatusPill status={task.status} />
        </Link>
    );
}

/** A titled panel of task rows; `tasks === null` is loading, a string is an error. */
export function TaskPanel({ title, tasks, members, empty, action }: {
    title: string; tasks: Task[] | null | string; members: ProjectMember[]; empty: string; action?: React.ReactNode;
}) {
    const id = `pd-panel-${title.replace(/\W+/g, '-').toLowerCase()}`;
    return (
        <section className="pd-panel" aria-labelledby={id}>
            <div className="pd-panel-heading">
                <h2 id={id}>{title}</h2>
                {Array.isArray(tasks) && <span className="pd-count">{tasks.length}</span>}
                {action}
            </div>
            {tasks === null ? <p className="pd-panel-empty">Loading…</p>
                : typeof tasks === 'string' ? <p className="pd-panel-empty" role="alert">{tasks}</p>
                : tasks.length === 0 ? <p className="pd-panel-empty">{empty}</p>
                : tasks.map(t => <TaskRow key={t.id} task={t} members={members} />)}
        </section>
    );
}

export function MemberList({ members, ownerId }: { members: ProjectMember[]; ownerId?: string }) {
    return (
        <div>
            {members.map(m => {
                const owner = m.role === 'owner' || m.user_id === ownerId;
                return (
                    <div key={m.id} className="pd-member-row">
                        <span className={`pd-avatar${owner ? ' pd-avatar--owner' : ''}`} aria-hidden="true">{initials(m)}</span>
                        <span><strong>{memberName(m)}</strong><small>{owner ? 'Project owner' : ROLE_LABEL[m.role]}</small></span>
                    </div>
                );
            })}
        </div>
    );
}
